import {useQueries, useQueryClient, type UseQueryOptions, type UseQueryResult} from '@tanstack/react-query'
import {useMemo, useRef} from 'react'

import {apiGetPaginatedItems} from '../../../api/memex-items/api-get-paginated-items'
import type {GetPaginatedItemsRequest} from '../../../api/memex-items/contracts'
import {createMemexItemModel} from '../../../models/memex-item-model'
import {getGroupValueForGroupId, setQueryDataForMemexItemsPage, setSliceByQueryData} from '../query-client-api'
import type {GroupId, PaginatedMemexItemsQueryKey} from './query-keys'
import {buildMemexItemsPageQueryKey, groupIdForUngroupedData} from './query-keys'
import {
  type ActiveQueriesMetadata,
  type MemexGroupsPageQueryData,
  type MemexPageQueryData,
  type PageParam,
  pageParamForInitialPage,
  pageParamForNextPlaceholder,
  type PaginatedMemexItemsQueryVariables,
} from './types'
import {useMemexItemsActiveQueriesMetadata} from './use-memex-items-active-queries-metadata'
import {nextPlaceholderPageInfo, useNextPlaceholderQuery} from './use-next-placeholder-query'

type UsePaginatedMemexItemsActiveQueriesReturnType = {
  queryKeys: Array<PaginatedMemexItemsQueryKey>
  queries: Array<UseQueryResult<MemexPageQueryData>>
}

/**
 * Based on data stored in the `activeQueriesMetadata` query, this hook
 * builds up a list of options to pass to `useQueries`, so that we can dynamically
 * monitor multiple queries representing memex items data.
 * @param variables PaginatedMemexItemsQueryVariables to use in QueryKey
 * @returns UsePaginatedMemexItemsActiveQueriesReturnType
 */
export function usePaginatedMemexItemsActiveQueries(
  variables: PaginatedMemexItemsQueryVariables,
): UsePaginatedMemexItemsActiveQueriesReturnType {
  const {activeQueriesMetadata} = useMemexItemsActiveQueriesMetadata(variables)
  const queryClient = useQueryClient()
  const {updateMemexItemsNextPlaceholderQuery, updateMemexGroupsNextPlaceholderQuery} =
    useNextPlaceholderQuery(variables)

  // Build up a set of options to pass to useQueries
  const queries = useMemo(() => {
    const options: Array<UseQueryOptions<MemexPageQueryData>> = []
    // This will be a flat list of query keys (in this case just the groupId + pageIndex pairs) for
    // all queries/pages that we want to monitor.
    const queryKeys = buildQueryKeysFromActiveQueriesMetadata(activeQueriesMetadata)
    for (const {groupId, pageParam} of queryKeys) {
      options.push({
        queryKey: buildMemexItemsPageQueryKey(variables, groupId, pageParam),
        queryFn: async ({signal}) => {
          if (pageParam === pageParamForNextPlaceholder) {
            // We never want to be making a request for this query, as it is just a placeholder
            return {nodes: [], pageInfo: nextPlaceholderPageInfo, totalCount: {value: 0, isApproximate: false}}
          }
          const groupValue = getGroupValueForGroupId(queryClient, groupId)
          const paginatedFetchOptions: GetPaginatedItemsRequest = {...variables, ...pageParam}
          if (variables.verticalGroupedByColumnId) {
            paginatedFetchOptions.verticalGroupedByGroupValue = groupValue
          } else if (variables.horizontalGroupedByColumnId) {
            paginatedFetchOptions.groupedByGroupValue = groupValue
          }
          const response = await apiGetPaginatedItems(paginatedFetchOptions, signal)
          // if we abort, then there is nothing to do with what was fetched, so return dummy content to appease
          // typescript (see https://tanstack.com/query/latest/docs/framework/react/guides/query-cancellation)
          if (signal.aborted) {
            return {nodes: [], pageInfo: nextPlaceholderPageInfo, totalCount: {value: 0, isApproximate: false}}
          }
          if ('slices' in response && response.slices) {
            setSliceByQueryData(queryClient, variables, {slices: response.slices})
          }
          // How we handle the response, will depend on if the data is grouped or not.
          if ('groups' in response) {
            // Grouped responses contain the first page of results for multiple groups.
            // We need to manually update the query cache to capture this.

            // We also need to keep track of queryData for the groups as a whole.
            const groupPageQueryData: MemexGroupsPageQueryData = {
              groups: [],
              pageInfo: response.pageInfo,
              totalCount: response.totalCount,
            }

            // Loop over the groups in the response
            for (const group of response.groups) {
              // Grouped responses contain the first page for each group
              const {items, ...groupsPageWithoutItems} = group
              // Seed an items page query with this group's items data
              setQueryDataForMemexItemsPage(queryClient, variables, group.groupId, pageParamForInitialPage, {
                nodes: items.nodes.map(item => createMemexItemModel(item)),
                pageInfo: items.pageInfo,
                totalCount: items.totalCount,
              })
              // Keep the rest of the group data for the groups page query
              groupPageQueryData.groups.push(groupsPageWithoutItems)
            }
            // Handle next_placeholder data for top-level groups, if any exist
            updateMemexGroupsNextPlaceholderQuery(response)

            // Return the group page queryData
            return groupPageQueryData
          } else {
            // Handle next_placeholder data for items, if any exist
            updateMemexItemsNextPlaceholderQuery(response, groupId)
            // If our data is ungrouped, then we can just return the response as-is.
            return {
              ...response,
              nodes: response.nodes.map(item => createMemexItemModel(item)),
            }
          }
        },
        structuralSharing: false,
        staleTime: Infinity,
      })
    }
    return options
  }, [
    activeQueriesMetadata,
    queryClient,
    updateMemexGroupsNextPlaceholderQuery,
    updateMemexItemsNextPlaceholderQuery,
    variables,
  ])

  const queryKeys = useMemo(() => queries.map(q => (q.queryKey as PaginatedMemexItemsQueryKey) || []), [queries])
  const queryResults = useQueries({
    queries,
  })
  const memoizedQueries = useQueriesMemo(queryResults)

  // The queries that are returned by `useQueries` do not have any information about their own query keys,
  // so we return the queryKeys alongside them so that downstream consumers can map back and forth by index
  return {
    queries: memoizedQueries,
    queryKeys,
  }
}

/**
 * Builds a set of query keys based on an ActiveQueriesMetadata object.
 * For grouped and ungrouped data, we return an array of {groupId: groupIdForUngroupedData, pageParam: pageParam} for each entry
 * in our activeQueriesMetadata.pageParams.
 * For grouped data, we also return keys for each individual group's pageParams.
 * We do this by building a map of groupId -> pageParams, and then returning
 * {groupId: groupId, pageParam: pageParam} for each pageParam of each group.
 */
function buildQueryKeysFromActiveQueriesMetadata(
  activeQueriesMetadata: ActiveQueriesMetadata,
): Array<{groupId: GroupId; pageParam: PageParam}> {
  if ('groups' in activeQueriesMetadata) {
    const queryKeysForItems = Object.entries(activeQueriesMetadata.groups).flatMap(([groupId, pageParams]) =>
      pageParams.map(pageParam => ({groupId, pageParam})),
    )
    const queryKeysForGroups = activeQueriesMetadata.pageParams.map(pageParam => ({
      groupId: groupIdForUngroupedData,
      pageParam,
    }))
    return [...queryKeysForItems, ...queryKeysForGroups]
  } else {
    return activeQueriesMetadata.pageParams.map(pageParam => ({groupId: groupIdForUngroupedData, pageParam}))
  }
}

/**
 * `useQueries` returns an array of `UseQueryResult` objects. The array returned here
 * will be a new object each time, which makes it impossible the use the return value
 * in the dependencies of any downstream `useMemo`s. This function works around
 * this behavior by using a custom algorithm for determining whether or not to return a new array
 * based on `data` attributes of each item in the array, which is what we want to use
 * to indicate we need to re-memoize because something about the items has changed.
 */
function useQueriesMemo(queriesResultArray: Array<UseQueryResult<MemexPageQueryData>>) {
  // this holds reference to previous value - it will only change if
  // the number of queries in the array changes or the data of a query changes
  const stableQueriesArrayRef = useRef<Array<UseQueryResult<MemexPageQueryData>>>([])
  // check if the queries length is the same and the data in each query is the same
  const areArraysConsideredTheSame =
    stableQueriesArrayRef.current && queriesResultArray.length === stableQueriesArrayRef.current.length
      ? queriesResultArray.every((element, i) => {
          const dataAtIndexIsTheSame =
            stableQueriesArrayRef.current && element.data === stableQueriesArrayRef.current[i]?.data

          if (dataAtIndexIsTheSame) {
            // Even though the data is the same, we could have a new query result here, so let's update the reference
            // of the stableQueriesArrayRef for this query result.

            // If the data is not the same, then we will replace the entire stableQueriesArrayRef below.
            stableQueriesArrayRef.current[i] = element
          }

          return dataAtIndexIsTheSame
        })
      : false

  //only update the ref tracking the queries array if they are different
  if (!areArraysConsideredTheSame) {
    stableQueriesArrayRef.current = queriesResultArray
  }
  return stableQueriesArrayRef.current
}
